import { type FC, type MouseEvent, useEffect, useRef, useState } from "react";
import { Breakpoint, useMediaQuery } from "@stubhub/broadway-providers";
import { Modal, ModalBody, ModalHeader, ModalSize, ModalTitle } from "@stubhub/broadway-web";
import { Content } from "src/shared/components/content";
import { PopoverComponent } from "src/shared/components/custom-popover";
import type { PageEventKey } from "src/shared/constants/PageEventKeys";
import type { PageEventType } from "src/shared/constants/PageEventTypes";
import { useStorePageEvent } from "src/shared/hooks/useStorePageEvent";
import { SeeMore, SeeMoreLinkStyle, TextWrapper } from "./TruncateText.styled";

interface Props {
  /** text to truncate */
  text: string;
  /** text to show in tablet popover */
  popoverText?: string;
  /** max lines before truncate */
  maxLines?: number;
  /** (default: false) Instead of expanding downward, the "seen more" button will open a modal with the content */
  isModal?: boolean;
  /** (default: null) optional: pass args for pageEventFunction to be called on "see more" click */
  pageEventArgs?: [peType: PageEventType, peKey: PageEventKey, peValue: string | undefined] | null;
  /** title of modal */
  modalTitle?: string;
  /** true if you want to display see more cta on a new line below the text */
  seeMoreOnNewLine?: boolean;
  /** background color of the inline "See more" overlay (default: "white") */
  seeMoreBackground?: string;
}

/**
 *
 * Truncate text if over maxLines.
 *
 * Default Behavior: Show popover when tablet and "see more" button for mobile which expands container downward on click.
 *
 * isModal Behavior: No Popover, always show See More and open modal on click.
 */
export const TruncateText: FC<Props> = ({
  text,
  popoverText,
  maxLines = 1,
  isModal = false,
  modalTitle,
  pageEventArgs = null,
  seeMoreOnNewLine = false,
  seeMoreBackground,
}) => {
  const storePageEvent = useStorePageEvent();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const textRef = useRef<HTMLDivElement>(null);

  const isMobile = !useMediaQuery(Breakpoint.Tablet);

  useEffect(() => {
    const textElement = textRef.current;
    if (!(textElement instanceof HTMLElement)) {
      return;
    }
    const { lineHeight } = window.getComputedStyle(textElement);
    if (!lineHeight) {
      return;
    }
    const lineHeightNum = Number(lineHeight.slice(0, -2));
    // Temporarily remove overflow:hidden so scrollHeight reflects the full
    // text height rather than the clamped height (some browsers report
    // scrollHeight === clientHeight when overflow is hidden).
    textElement.style.overflow = "visible";
    const overflows =
      textElement.scrollHeight >= textElement.clientHeight + lineHeightNum ||
      textElement.scrollWidth > textElement.clientWidth;
    textElement.style.overflow = "";
    if (overflows) {
      setIsOverflowing(true);
    }
  }, [text]);

  const handleSeeMoreClick = (e: MouseEvent<HTMLSpanElement>) => {
    e.preventDefault();
    if (pageEventArgs?.length === 3) {
      storePageEvent(...pageEventArgs);
    }
    if (isModal) {
      setShowModal(true);
    } else {
      setIsExpanded(!isExpanded);
    }
  };

  const truncatedText = seeMoreOnNewLine ? (
    <div>
      <TextWrapper ref={textRef} isExpanded={isExpanded} maxLines={maxLines}>
        {text}
      </TextWrapper>
      {isOverflowing && !isExpanded && (isMobile || isModal) && (
        <SeeMoreLinkStyle onClick={handleSeeMoreClick}>
          {/* "Read more" */}
          <Content id="cid$#3718286#" />
        </SeeMoreLinkStyle>
      )}
    </div>
  ) : (
    <TextWrapper ref={textRef} isExpanded={isExpanded} maxLines={maxLines}>
      {text}
      {isOverflowing && !isExpanded && (isMobile || isModal) && (
        <SeeMore
          onClick={handleSeeMoreClick}
          {...(seeMoreBackground !== undefined && { $seeMoreBackground: seeMoreBackground })}
        >
          {/* "See more" */}
          <Content id="cid$#3337656#" />
        </SeeMore>
      )}
    </TextWrapper>
  );

  const showPopoverForTablet = !isMobile && isOverflowing && !isExpanded && !isModal;

  return (
    <>
      {isModal && (
        <Modal
          isOpen={showModal}
          onOpenChange={(open) => {
            if (!open) setShowModal(false);
          }}
          dismissible
          size={ModalSize.Large}
        >
          <ModalHeader>{!!modalTitle && <ModalTitle>{modalTitle}</ModalTitle>}</ModalHeader>
          <ModalBody>{text}</ModalBody>
        </Modal>
      )}
      {showPopoverForTablet ? (
        <PopoverComponent renderPopoverTrigger={() => truncatedText} popoverContent={popoverText ?? text} />
      ) : (
        truncatedText
      )}
    </>
  );
};
